import { useEffect, useMemo, useState } from 'react'
import BundleExperienceField, { type BundleExperienceValue } from '../components/BundleExperienceField'
import { useNavigate } from 'react-router-dom'
import {
  CubeIcon,
  PlusIcon,
  CommandLineIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  listBundles,
  createBundle,
  deleteBundle,
  generateBundleToken,
  fetchTenantApps,
  fetchPublicCatalogPage,
  type Bundle,
  type BundleApp,
  type BundleItemAction,
} from '../services/api'
import {
  PageHeader,
  EmptyState,
  Spinner,
  Badge,
  ConfirmDialog,
  Modal,
  Field,
  SearchInput,
} from '../components/ui'
import { errorText } from '../utils/errorText'
import CommandBlock from '../components/CommandBlock'

/** How long a command minted from the card stays valid. The detail page is where a different
 *  expiry or a use cap is chosen; the card mints the ordinary case in one click. */
const CARD_TOKEN_HOURS = 24

const ACTION_TONE: Record<string, 'green' | 'blue' | 'red'> = {
  install: 'green',
  update: 'blue',
  uninstall: 'red',
}

const dateOnly = (iso: string | null) =>
  iso
    ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—'

const dateTime = (iso: string | null) =>
  iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString() : '—'

/** /apps returns current_version as the whole version JSON; other endpoints flatten it to the
 *  string. Rendering the object throws React error #31 and unmounts the page, so read the
 *  version out of either shape and never hand a bare object to JSX. */
const versionLabel = (current: unknown): string | null => {
  if (typeof current === 'string') return current || null
  if (current && typeof current === 'object') {
    const version = (current as { version?: unknown }).version
    return typeof version === 'string' ? version || null : null
  }
  return null
}

/** The public catalog package a library entry is: a subscription's own id, or the package a
 *  copy was made from. */
const publicIdOf = (a: any): string | null =>
  a.public_app_id ??
  (typeof a.package_source === 'string' && a.package_source.startsWith('public_catalog:')
    ? a.package_source.slice('public_catalog:'.length)
    : null)

/** Every package a bundle can carry: the tenant's library, then each public catalog package the
 *  library does not already hold, sorted by name. */
export function bundlePickerApps(library: any[], catalog: any[]): any[] {
  const held = new Set<string>()
  for (const a of library) {
    held.add(String(a.id))
    const pub = publicIdOf(a)
    if (pub) held.add(pub)
  }
  const fromCatalog = catalog
    .filter((p) => !held.has(String(p.id)))
    .map((p) => ({ ...p, current_version: p.version ?? null }))
  const nameOf = (a: any) => (a.display_name || a.name || '').toLowerCase()
  return [...library, ...fromCatalog].sort((x, y) => nameOf(x).localeCompare(nameOf(y)))
}

/** Every page of a paged list; the routes cap a page at 100. */
async function allPages(fetch: (p: { limit: number; offset: number }) => Promise<{ apps: any[]; total: number }>) {
  const page = 100
  const all: any[] = []
  for (let offset = 0; ; offset += page) {
    const r = await fetch({ limit: page, offset })
    all.push(...r.apps)
    if (r.apps.length < page || all.length >= r.total) break
  }
  return all
}

export default function Bundles() {
  const [bundles, setBundles] = useState<Bundle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<Bundle | null>(null)

  const refresh = async () => {
    try {
      const { bundles } = await listBundles()
      setBundles(bundles)
      setError(null)
    } catch (err) {
      setError(errorText(err, 'Failed to load bundles'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const remove = async () => {
    if (!toDelete) return
    try {
      await deleteBundle(toDelete.id)
      refresh()
    } catch (err) {
      setError(errorText(err, 'Failed to delete bundle'))
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Bundles"
        subtitle="Bundle packages with install/update and uninstall actions, then generate a one-liner CLI command to deploy them on any Windows machine — no Intune required."
        actions={
          <button className="btn-green" onClick={() => setCreating(true)}>
            <PlusIcon className="h-4 w-4" /> Create Bundle
          </button>
        }
      />

      {error && (
        <div className="mb-5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24 text-fmuted">
          <Spinner className="h-6 w-6" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<CubeIcon className="h-7 w-7" />}
            title="No bundles yet"
            hint="Bundles let you group packages together and generate a single CLI command to install or uninstall them on any Windows machine."
            action={
              <button className="btn-green mt-1" onClick={() => setCreating(true)}>
                <PlusIcon className="h-4 w-4" /> Create your first bundle
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {bundles.map((b) => (
            <BundleCard
              key={b.id}
              bundle={b}
              onDelete={() => setToDelete(b)}
              onChanged={refresh}
              onError={setError}
            />
          ))}
        </div>
      )}

      {creating && (
        <CreateBundleModal onClose={() => setCreating(false)} onCreated={refresh} />
      )}

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        title="Delete bundle"
        message={`Delete the bundle "${toDelete?.name}"? The generated CLI command will stop working.`}
        confirmLabel="Delete bundle"
      />
    </div>
  )
}

function BundleCard({
  bundle,
  onDelete,
  onChanged,
  onError,
}: {
  bundle: Bundle
  onDelete: () => void
  onChanged: () => void
  onError: (message: string) => void
}) {
  const navigate = useNavigate()
  const [minting, setMinting] = useState(false)
  // A portal can be newer than the backend it talks to for the length of a deploy, and a missing
  // list must not blank the card.
  const apps = bundle.apps || []

  const generate = async () => {
    setMinting(true)
    try {
      await generateBundleToken(bundle.id, { expiry_hours: CARD_TOKEN_HOURS, max_uses: null })
      onChanged()
    } catch (err) {
      onError(errorText(err, 'Failed to generate the command'))
    } finally {
      setMinting(false)
    }
  }

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 text-accent">
            <CubeIcon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-ftext">{bundle.name}</h3>
            {bundle.description && <p className="text-sm text-fmuted">{bundle.description}</p>}
            <p className="mt-1 text-[12px] text-ffaint">
              {bundle.item_count} app{bundle.item_count !== 1 ? 's' : ''}
              {bundle.created_by ? ` · created by ${bundle.created_by}` : ''} ·{' '}
              {dateOnly(bundle.created_at)}
            </p>
            {/* An admin has to be able to tell a bundle that prompts from one that installs
                silently without opening it — they are the same card otherwise. */}
            <p className="mt-1 text-[12px] text-ffaint">
              {bundle.ux_template_name
                ? `Experience: ${bundle.ux_template_name}`
                : 'Experience: silent'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* The mock's Run button drives nothing on a server: a bundle runs where the machine is,
              from the command below. Details is the action that exists — the page where apps,
              their actions and the SCCM export live. */}
          <button className="btn-outline btn-sm" onClick={() => navigate(`/bundles/${bundle.id}`)}>
            Details
          </button>
          <button
            onClick={onDelete}
            title="Delete bundle"
            aria-label={`Delete ${bundle.name}`}
            className="rounded-md p-1.5 text-fmuted hover:bg-red-500/10 hover:text-red-500 dark:hover:text-red-300"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {apps.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {apps.map((a: BundleApp) => (
            <span
              key={a.id}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-panel3 px-2.5 py-1 text-[13px] text-ftext"
            >
              {a.app_name}
              <Badge tone={ACTION_TONE[a.action] ?? 'slate'}>{a.action}</Badge>
            </span>
          ))}
        </div>
      )}

      <div>
        <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-ffaint">
          <CommandLineIcon className="h-3.5 w-3.5" /> One-liner CLI command
          {/* A command that has expired is worse than none: it copies cleanly and fails on the
              machine it was meant for, so the card says how long this one has. */}
          {bundle.command_expires_at && (
            <span className="ml-auto font-normal">
              Expires {dateTime(bundle.command_expires_at)}
            </span>
          )}
        </p>
        {bundle.cli_command ? (
          // Summarised, not printed: the one-liner is a download URL and a JWT, several hundred
          // characters of which none is worth reading off a card. Show opens it; copy carries it.
          <CommandBlock
            command={bundle.cli_command}
            summary="PowerShell, elevated — downloads the agent, runs the bundle, removes it"
            copyLabel="Copy CLI command"
          />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-line bg-base px-3 py-2">
            <span className="text-[12px] text-fmuted">
              No command yet — a run token is what makes the one-liner work.
            </span>
            <button className="btn-outline btn-sm" onClick={generate} disabled={minting}>
              {minting ? 'Generating…' : `Generate command (${CARD_TOKEN_HOURS}h)`}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function CreateBundleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  // The action per picked app. Absent means install or update, which is what every bundle
  // created before actions could be chosen here carried.
  const [actions, setActions] = useState<Map<string, BundleItemAction>>(new Map())
  const [query, setQuery] = useState('')
  const [apps, setApps] = useState<any[]>([])
  // Until the packages are read, the picker says it is loading rather than "No packages match".
  const [appsLoading, setAppsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [experience, setExperience] = useState<BundleExperienceValue>({
    templateId: null,
    overrides: null,
  })

  useEffect(() => {
    // The picker searches client-side, so it needs every package: the library and the public
    // catalog, each read page by page. Without the catalog the library alone is offered.
    Promise.all([
      allPages(fetchTenantApps),
      allPages(fetchPublicCatalogPage).catch(() => {
        setError('The public catalog could not be read; only your library is listed.')
        return []
      }),
    ])
      .then(([library, catalog]) => setApps(bundlePickerApps(library, catalog)))
      .catch((err) => setError(errorText(err, 'Failed to load your packages')))
      .finally(() => setAppsLoading(false))
  }, [])

  const PICKER_ROWS = 40
  const matching = useMemo(() => {
    const q = query.toLowerCase()
    return apps.filter((a) => (a.display_name || a.name || '').toLowerCase().includes(q))
  }, [apps, query])
  const filtered = matching.slice(0, PICKER_ROWS)

  const toggle = (id: string) => {
    setPicked((s) => {
      const next = new Set(s)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const submit = async () => {
    if (!name.trim()) return setError('Give the bundle a name')
    if (picked.size === 0) return setError('Pick at least one package')
    setSaving(true)
    try {
      // Created with its apps in one call: a bundle that exists but is empty is a state nobody
      // asked for, and it is what a failed second request used to leave behind.
      await createBundle({
        name: name.trim(),
        description: description.trim() || null,
        apps: Array.from(picked).map((app_id) => ({ app_id, action: actions.get(app_id) ?? 'install' })),
        ux_template_id: experience.templateId,
        ux_overrides_json: experience.overrides,
      })
      onCreated()
      onClose()
    } catch (err) {
      setError(errorText(err, 'Failed to create bundle'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Create Bundle"
      footer={
        <>
          <button className="btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-green" onClick={submit} disabled={saving}>
            {saving ? 'Creating…' : 'Create bundle'}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      <Field label="Bundle name">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Developer Baseline"
          autoFocus
        />
      </Field>
      <Field label="Description">
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this bundle installs"
        />
      </Field>
      <Field label="Deployment experience">
        <BundleExperienceField value={experience} onChange={setExperience} disabled={saving} />
      </Field>
      <Field label={`Apps (${picked.size} selected)`}>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Filter packages…"
          className="mb-2"
        />
        <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-line p-1">
          {appsLoading ? (
            <p className="flex items-center gap-2 px-2 py-3 text-sm text-fmuted">
              <Spinner className="h-4 w-4" /> Loading packages…
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-3 text-sm text-fmuted">No packages match.</p>
          ) : (
            filtered.map((a) => (
              <label
                key={a.id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-theme-tertiary/50"
              >
                <input
                  type="checkbox"
                  checked={picked.has(a.id)}
                  onChange={() => toggle(a.id)}
                  className="h-4 w-4 rounded border-line2 bg-panel3 text-accent focus:ring-accent"
                />
                <span className="text-sm text-ftext">{a.display_name || a.name}</span>
                {versionLabel(a.current_version) && (
                  <span className="ml-auto text-xs text-ffaint">
                    v{versionLabel(a.current_version)}
                  </span>
                )}
                {picked.has(a.id) && (
                  <select
                    aria-label={`Action for ${a.display_name || a.name}`}
                    className="input ml-2 w-auto py-0.5 text-xs"
                    value={actions.get(a.id) ?? 'install'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setActions((m) => new Map(m).set(a.id, e.target.value as BundleItemAction))
                    }
                  >
                    <option value="install">Install or update</option>
                    <option value="update">Update only</option>
                    <option value="uninstall">Uninstall</option>
                  </select>
                )}
              </label>
            ))
          )}
        </div>
        {!appsLoading && matching.length > PICKER_ROWS && (
          <p className="mt-1 text-[12px] text-fmuted">
            Showing {PICKER_ROWS} of {matching.length} packages. Filter to find the others.
          </p>
        )}
      </Field>
    </Modal>
  )
}
